import React, {useCallback, useMemo, useRef, useState} from "react";
import {Alert, Badge, Button, Form, Modal, ProgressBar, Spinner, Table} from "react-bootstrap";
import {toast} from "react-toastify";
import {adminCheckAmazonAvailability, fetchBooksWithParams} from "../../http/bookAPI";
import {getRedirectUrlApi} from "../../http/userAPI";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const statusVariant = (status) => {
    if (status === "alive") return "success";
    if (status === "deleted") return "danger";
    if (status === "manual_required") return "warning";
    if (status === "checking") return "primary";
    if (status === "error") return "dark";
    return "secondary";
};

const money = (value, prefix = "") => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? `${prefix}${number.toFixed(prefix ? 2 : 0)}` : `${prefix}0`;
};

const formatPrices = (prices) => {
    const entries = [
        ["K", prices?.kindle],
        ["PB", prices?.paperback],
        ["HC", prices?.hardcover],
        ["AU", prices?.audio],
    ].filter(([, value]) => value !== undefined && value !== null && value !== "");
    return entries.length ? entries.map(([label, value]) => `${label}: $${Number(value).toFixed(2)}`).join(" | ") : "-";
};

const bookToRow = (book) => ({
    id: book.id,
    title: book.title,
    ownerId: book.userId,
    status: "queued",
    action: "-",
    note: "Waiting",
    noteUrl: null,
    refundVirt: 0,
    refundUsd: 0,
    prices: null,
});

const AdminAmazonAvailabilityAudit = () => {
    const [show, setShow] = useState(false);
    const [dryRun, setDryRun] = useState(true);
    const [delayMs, setDelayMs] = useState(4500);
    const [requestDelayMs, setRequestDelayMs] = useState(900);
    const [maxBooks, setMaxBooks] = useState(10);
    const [lastUpdateOlderThanDays, setLastUpdateOlderThanDays] = useState(30);
    const pageSize = 50;
    const [loadingBooks, setLoadingBooks] = useState(false);
    const [running, setRunning] = useState(false);
    const [rows, setRows] = useState([]);
    const [progress, setProgress] = useState({done: 0, total: 0});
    const stopRef = useRef(false);

    const stats = useMemo(() => rows.reduce((acc, row) => {
        acc[row.status] = (acc[row.status] || 0) + 1;
        acc.refundVirt += Number(row.refundVirt || 0);
        acc.refundUsd += Number(row.refundUsd || 0);
        return acc;
    }, {alive: 0, deleted: 0, manual_required: 0, error: 0, refundVirt: 0, refundUsd: 0}), [rows]);

    const loadLiveBooks = useCallback(async () => {
        setLoadingBooks(true);
        try {
            const targetCount = Math.max(1, Number(maxBooks) || 10);
            const staleDays = Math.max(0, Number(lastUpdateOlderThanDays) || 0);
            const baseParams = {
                status: "LIVE",
                amazon_last_update_older_than_days: staleDays,
            };
            const first = await fetchBooksWithParams({...baseParams, page: 1, limit: pageSize});
            const items = Array.isArray(first?.items) ? first.items : [];
            const total = Number(first?.count || items.length);
            let all = items;
            const pages = Math.ceil(total / pageSize);
            for (let page = 2; page <= pages && all.length < targetCount; page += 1) {
                const next = await fetchBooksWithParams({...baseParams, page, limit: pageSize});
                all = all.concat(Array.isArray(next?.items) ? next.items : []);
            }
            const nextRows = all.slice(0, targetCount).map(bookToRow);
            setRows(nextRows);
            setProgress({done: 0, total: nextRows.length});
            if (!nextRows.length) toast.info("No LIVE books found.");
            return nextRows;
        } catch (e) {
            toast.error(e?.message || "Failed to load LIVE books.");
            return [];
        } finally {
            setLoadingBooks(false);
        }
    }, [lastUpdateOlderThanDays, maxBooks]);

    const patchRow = useCallback((id, patch) => {
        setRows((prev) => prev.map((row) => (row.id === id ? {...row, ...patch} : row)));
    }, []);

    const openAmazonNote = useCallback(async (row) => {
        if (!row.noteUrl) return;
        try {
            const response = await getRedirectUrlApi(row.noteUrl);
            if (response?.redirectUrl) {
                window.open(response.redirectUrl, "_blank", "noopener noreferrer");
                return;
            }
            toast.error("Failed to build redirect URL.");
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to open Amazon link.");
        }
    }, []);

    const startAudit = useCallback(async () => {
        const runnableStatuses = dryRun
            ? new Set(["queued", "error"])
            : new Set(["queued", "error", "alive", "deleted", "manual_required"]);
        const targetCount = Math.max(1, Number(maxBooks) || 10);
        let queue = rows.filter((row) => runnableStatuses.has(row.status)).slice(0, targetCount);
        if (!queue.length) {
            const loadedRows = await loadLiveBooks();
            queue = loadedRows.filter((row) => runnableStatuses.has(row.status)).slice(0, targetCount);
            if (!queue.length) return;
        }

        stopRef.current = false;
        setRunning(true);
        setProgress({done: 0, total: queue.length});

        for (let index = 0; index < queue.length; index += 1) {
            if (stopRef.current) break;
            const row = queue[index];
            patchRow(row.id, {status: "checking", action: "checking", note: "Checking Amazon...", noteUrl: null});
            try {
                const result = await adminCheckAmazonAvailability(row.id, {dryRun, requestDelayMs});
                const availableCheck = Array.isArray(result.checks)
                    ? result.checks.find((check) => check.available && check.url)
                    : null;
                patchRow(row.id, {
                    status: result.status,
                    action: result.action,
                    note: result.firstAvailable
                        ? `${result.firstAvailable.marketplace}/${result.firstAvailable.format}/${result.firstAvailable.asin}`
                        : (result.checks?.[0]?.reason || result.reason || "-"),
                    noteUrl: availableCheck?.url || null,
                    refundVirt: result.refunds?.refundVirt || 0,
                    refundUsd: result.refunds?.refundUsd || 0,
                    checked: result.checks?.length || 0,
                    prices: result.updatedPrices || result.prices || null,
                });
            } catch (e) {
                patchRow(row.id, {
                    status: "error",
                    action: "failed",
                    note: e?.response?.data?.message || e?.message || "Request failed",
                    noteUrl: null,
                });
            }
            setProgress({done: index + 1, total: queue.length});
            if (index < queue.length - 1 && !stopRef.current) {
                await sleep(Math.max(500, Number(delayMs) || 4500));
            }
        }

        setRunning(false);
    }, [delayMs, dryRun, loadLiveBooks, maxBooks, patchRow, requestDelayMs, rows]);

    const stopAudit = useCallback(() => {
        stopRef.current = true;
        setRunning(false);
    }, []);

    const resetAuditState = useCallback(() => {
        stopRef.current = false;
        setRows([]);
        setProgress({done: 0, total: 0});
    }, []);

    const handleClose = useCallback(() => {
        if (running) return;
        resetAuditState();
        setShow(false);
    }, [resetAuditState, running]);

    const percent = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

    return (
        <>
            <Button variant="outline-success" size="sm" onClick={() => setShow(true)}>
                Amazon Availability Audit
            </Button>

            <Modal
                show={show}
                onHide={handleClose}
                size="xl"
                top
                dialogClassName="amazon-audit-modal"
            >
                <Modal.Header closeButton={!running} className="py-2 px-3">
                    <Modal.Title className="h5 mb-0">Amazon Availability Audit</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-3 px-3">
                    <Alert variant="light" className="border small py-2 px-3 mb-3">
                        Checks LIVE books through Scraperfly with a pause between requests. Alive books keep LIVE status and get refreshed prices/date. Fully unavailable books become DELETED only in apply mode; open assignments are canceled and owner funds are returned.
                    </Alert>

                    <div className="d-flex flex-wrap align-items-end gap-2 mb-2 small">
                        <Form.Check
                            type="switch"
                            id="amazon-audit-dry-run"
                            label="Dry run"
                            checked={dryRun}
                            disabled={running}
                            onChange={(e) => setDryRun(e.target.checked)}
                        />
                        <Form.Group style={{width: 120}}>
                            <Form.Label className="small mb-1">Book gap, ms</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={500}
                                step={500}
                                value={delayMs}
                                disabled={running}
                                onChange={(e) => setDelayMs(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group style={{width: 130}}>
                            <Form.Label className="small mb-1">Request gap, ms</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={0}
                                max={10000}
                                step={250}
                                value={requestDelayMs}
                                disabled={running}
                                onChange={(e) => setRequestDelayMs(Number(e.target.value) || 0)}
                            />
                        </Form.Group>
                        <Form.Group style={{width: 110}}>
                            <Form.Label className="small mb-1">Check books</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                max={1000}
                                value={maxBooks}
                                disabled={running}
                                onChange={(e) => setMaxBooks(Number(e.target.value) || 10)}
                            />
                        </Form.Group>
                        <Form.Group style={{width: 150}}>
                            <Form.Label className="small mb-1">Last check older, days</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={0}
                                max={3650}
                                value={lastUpdateOlderThanDays}
                                disabled={running}
                                onChange={(e) => setLastUpdateOlderThanDays(Number(e.target.value) || 0)}
                            />
                        </Form.Group>
                        <Button variant="outline-secondary" size="sm" disabled={running || loadingBooks} onClick={loadLiveBooks}>
                            {loadingBooks ? <Spinner size="sm" /> : "Load LIVE Books"}
                        </Button>
                        <Button variant={dryRun ? "success" : "danger"} size="sm" disabled={running || loadingBooks} onClick={startAudit}>
                            {running ? "Running..." : dryRun ? "Start Dry Run" : "Start and Apply"}
                        </Button>
                        {running && (
                            <Button variant="outline-danger" size="sm" onClick={stopAudit}>
                                Stop
                            </Button>
                        )}
                    </div>

                    <ProgressBar now={percent} label={`${progress.done}/${progress.total}`} className="mb-2" style={{height: 18}} />

                    <div className="d-flex flex-wrap gap-2 mb-2 small">
                        <Badge bg="success">Alive: {stats.alive}</Badge>
                        <Badge bg="danger">Deleted: {stats.deleted}</Badge>
                        <Badge bg="warning" text="dark">Manual: {stats.manual_required}</Badge>
                        <Badge bg="dark">Errors: {stats.error}</Badge>
                        <Badge bg="secondary">Refund Virt: {money(stats.refundVirt)}V</Badge>
                        <Badge bg="secondary">Refund USD: {money(stats.refundUsd, "$")}</Badge>
                    </div>

                    <div className="amazon-audit-table-wrap">
                        <Table striped bordered hover size="sm" className="small align-middle mb-0 amazon-audit-table">
                            <thead>
                            <tr>
                                <th>ID</th>
                                <th>Title</th>
                                <th>Status</th>
                                <th>Action</th>
                                <th>Checked</th>
                                <th>Prices</th>
                                <th>Refund</th>
                                <th>Note</th>
                            </tr>
                            </thead>
                            <tbody>
                            {rows.length ? rows.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.id}</td>
                                    <td className="amazon-audit-title-cell">{row.title}</td>
                                    <td><Badge bg={statusVariant(row.status)}>{row.status}</Badge></td>
                                    <td>{row.action}</td>
                                    <td>{row.checked || "-"}</td>
                                    <td className="amazon-audit-prices-cell">{formatPrices(row.prices)}</td>
                                    <td>{money(row.refundVirt)}V / {money(row.refundUsd, "$")}</td>
                                    <td className="amazon-audit-note-cell">
                                        {row.noteUrl ? (
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 amazon-audit-note-link"
                                                onClick={() => openAmazonNote(row)}
                                                title="Open through Virtalibry redirect"
                                            >
                                                {row.note}
                                            </button>
                                        ) : row.note}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={8} className="text-center text-muted">Load LIVE books to start.</td>
                                </tr>
                            )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="py-2 px-3">
                    <Button variant="secondary" className="btn btn-sm" disabled={running} onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </Modal>
            <style>{`
                .amazon-audit-modal {
                    max-width: min(1540px, calc(100vw - 96px));
                    width: min(1540px, calc(100vw - 96px));
                }
                .amazon-audit-table-wrap {
                    max-height: 62vh;
                    overflow: auto;
                }
                .amazon-audit-table {
                    font-size: 0.74rem;
                    table-layout: fixed;
                }
                .amazon-audit-table th,
                .amazon-audit-table td {
                    padding: 0.16rem 0.26rem;
                    line-height: 1.08;
                    vertical-align: middle;
                    word-break: break-word;
                }
                .amazon-audit-table .badge {
                    font-size: 0.63rem;
                    padding: 0.18rem 0.34rem;
                    white-space: normal;
                }
                .amazon-audit-table th:nth-child(1) { width: 56px; }
                .amazon-audit-table th:nth-child(2) { width: 38%; }
                .amazon-audit-table th:nth-child(3) { width: 96px; }
                .amazon-audit-table th:nth-child(4) { width: 120px; }
                .amazon-audit-table th:nth-child(5) { width: 74px; }
                .amazon-audit-table th:nth-child(6) { width: 160px; }
                .amazon-audit-table th:nth-child(7) { width: 92px; }
                .amazon-audit-table th:nth-child(8) { width: 220px; }
                .amazon-audit-title-cell,
                .amazon-audit-prices-cell {
                    font-size: 0.72rem;
                }
                .amazon-audit-note-cell {
                    font-size: 0.7rem;
                }
                .amazon-audit-note-link {
                    font-size: inherit;
                    line-height: inherit;
                    vertical-align: baseline;
                }
            `}</style>
        </>
    );
};

export default AdminAmazonAvailabilityAudit;
